import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { useCallback, useEffect, useState } from 'react';
import { ProductRecommendationTypes, RelevanceModifier } from '../services/RecommendationService';
import { useMappingService, useRecommendationService } from './serviceHooks';
import { MappedProduct } from '../types/mapped';

const DEFAULT_PRODUCT_COUNT = 6;
const MAX_PRODUCT_COUNT = 20;

interface UseProductRecommendationOptions {
    productCount: number;
    recommendationType: ProductRecommendationTypes;
    isEnabled: boolean;
    displayLocation: string;
    categoryId?: string;
    masterProductItemId?: string;
    relevanceModifier?: RelevanceModifier;
    relevanceModifierStrength?: number;
    request: IRequestContext;
}

export const useProductRecommendations = ({
    isEnabled,
    request,
    productCount,
    displayLocation,
    recommendationType,
    categoryId,
    masterProductItemId,
    relevanceModifier,
    relevanceModifierStrength
}: UseProductRecommendationOptions) => {
    const [products, setProducts] = useState<MappedProduct[]>([]);
    const [isLoading, setIsLoading] = useState(isEnabled);

    const relewiseMappingService = useMappingService(request);
    const relewiseRecommendationService = useRecommendationService(request);

    const loadRecommendations = useCallback(async () => {
        if (!isEnabled) {
            return;
        }
        setIsLoading(true);

        const numberOfProducts = Math.min(productCount ?? DEFAULT_PRODUCT_COUNT, MAX_PRODUCT_COUNT);
        const products = await relewiseRecommendationService.getGenericProductRecommendations({
            recommendationType,
            displayLocation,
            categoryId,
            masterProductItemId,
            relevanceModifier,
            relevanceModifierStrength,
            count: numberOfProducts
        });

        const mappedProducts = relewiseMappingService.mapRelewiseProductResultList(products);
        setProducts(mappedProducts);

        setIsLoading(false);
    }, [
        recommendationType,
        displayLocation,
        categoryId,
        masterProductItemId,
        relevanceModifier,
        relevanceModifierStrength,
        productCount,
        isEnabled,
        relewiseMappingService,
        relewiseRecommendationService
    ]);

    useEffect(() => {
        loadRecommendations();
    }, [isEnabled, loadRecommendations]);

    return { products, isLoading };
};
